import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { LEGAL_INTRO, LEGAL_SECTIONS, LEGAL_ENTITY } from "@/data/legal";

/**
 * LegalPage — página reutilizable para /aviso-legal, /politica-de-privacidad y /politica-de-cookies.
 * Muestra el mismo contenido oficial, con título propio y sección destacada según la ruta.
 */
export default function LegalPage({ variant }) {
  const meta = VARIANTS[variant];

  return (
    <>
      <SEO
        title={meta.seoTitle}
        description={meta.seoDescription}
        path={meta.path}
      />
      <div className="pt-[76px] md:pt-[96px] bg-brand-bg min-h-screen">
        <section className="section-y">
          <div className="container-prose max-w-3xl">
            <p className="overline">Aviso legal</p>
            <h1 className="h2-display mt-2 mb-8">{meta.title}</h1>

            <div className="rounded-2xl border border-brand-line bg-white p-5 md:p-6 mb-8 text-sm text-brand-ink/80">
              <p className="font-outfit font-semibold text-brand-primary mb-2">
                Titular del sitio
              </p>
              <p>
                <strong>{LEGAL_ENTITY.name}</strong> · CIF {LEGAL_ENTITY.cif}<br />
                {LEGAL_ENTITY.address}<br />
                Tel. {LEGAL_ENTITY.phone} · {LEGAL_ENTITY.email}<br />
                <span className="text-brand-muted">
                  Inscrita en {LEGAL_ENTITY.registry}. {LEGAL_ENTITY.cicma}. Servicios comercializados por {LEGAL_ENTITY.club_name} (CIF {LEGAL_ENTITY.club_cif}).
                </span>
              </p>
            </div>

            <p className="text-sm text-brand-ink/75 leading-relaxed mb-10">
              {LEGAL_INTRO}
            </p>

            <div className="space-y-10">
              {LEGAL_SECTIONS.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  className={`scroll-mt-32 ${
                    s.id === meta.highlightId
                      ? "rounded-2xl bg-brand-surface/60 border border-brand-line p-6 md:p-7"
                      : ""
                  }`}
                >
                  <h2 className="font-outfit font-semibold text-brand-primary text-lg md:text-xl mb-3">
                    {s.title}
                  </h2>
                  {s.body?.map((p, i) => (
                    <p
                      key={i}
                      className="text-sm text-brand-ink/80 leading-relaxed mb-3 last:mb-0"
                    >
                      {p}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-3 space-y-1.5 text-sm text-brand-ink/80 list-disc pl-5">
                      {s.list.map((li, i) => (
                        <li key={i}>{li}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-14 pt-6 border-t border-brand-line text-xs text-brand-muted flex flex-col sm:flex-row justify-between gap-3">
              <p>© {new Date().getFullYear()} AMITOUR SL — Todos los derechos reservados.</p>
              <div className="flex items-center gap-3">
                <Link to="/aviso-legal" className="hover:text-brand-primary">Aviso legal</Link>
                <span>·</span>
                <Link to="/politica-de-privacidad" className="hover:text-brand-primary">Privacidad</Link>
                <span>·</span>
                <Link to="/politica-de-cookies" className="hover:text-brand-primary">Cookies</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

const VARIANTS = {
  legal: {
    title: "Aviso Legal",
    path: "/aviso-legal",
    highlightId: "web",
    seoTitle: "Aviso Legal · Club Amistad",
    seoDescription:
      "Aviso legal de Club Amistad — AMITOUR SL. Condiciones de uso del sitio web, propiedad intelectual, limitación de responsabilidad y ley aplicable.",
  },
  privacy: {
    title: "Política de Privacidad",
    path: "/politica-de-privacidad",
    highlightId: "finalidad",
    seoTitle: "Política de Privacidad · Club Amistad",
    seoDescription:
      "Política de privacidad de Club Amistad. Cómo tratamos tus datos, base legal, plazo de conservación y tus derechos según el RGPD.",
  },
  cookies: {
    title: "Política de Cookies",
    path: "/politica-de-cookies",
    highlightId: "cookies",
    seoTitle: "Política de Cookies · Club Amistad",
    seoDescription:
      "Política de cookies de Club Amistad. Tipos de cookies que usamos (necesarias, analíticas, marketing) y cómo puedes gestionarlas en cualquier momento.",
  },
};
